<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="'Create Order'" subTitle="Transactions" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Customer</label>
              <select class="form-select" v-model="form.customer_id" required>
                <option value="">Select Customer</option>
                <option v-for="c in customers" :key="c.customer_id" :value="c.customer_id">
                  {{ c.name || c.phone_number }}
                </option>
              </select>
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Total Price (Rp)</label>
              <input type="number" class="form-control" v-model="form.total_price" placeholder="0" min="0" required />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Klasifikasi Dokumen &amp; Standar Acuan</label>
              <select class="form-select" v-model="form.similarity_profile_id" @change="onProfileSelect">
                <option :value="null">-- Default (Artikel Jurnal) --</option>
                <option v-for="p in similarityProfiles" :key="p.id" :value="p.id">
                  {{ p.service_classification }} — {{ p.profile_name }} (Maks. {{ p.default_max_overall }}%)
                </option>
              </select>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Service Type</label>
              <select class="form-select" v-model="form.service_type" @change="onServiceTypeChange" required>
                <option value="">Select Service Type</option>
                <option value="PLAGIARISM">Plagiarism Check & AI</option>
                <option value="PARAPHRASE">Paraphrase</option>
                <option value="DOC_FIX">Document Fix</option>
                <option value="JOURNAL">Journal Publication</option>
                <option value="ALL">All Services (Combination)</option>
                <option value="PACKAGE">Package/Bundle</option>
              </select>
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4" v-if="form.service_type && form.service_type !== 'PACKAGE'">
              <label class="form-label text-dark fs-14 fw-medium">Service</label>
              <select class="form-select" v-model="form.service_id" required>
                <option value="">Select Service</option>
                <option v-for="s in services" :key="s.service_id" :value="s.service_id">
                  {{ s.service_name }}
                </option>
              </select>
            </div>

            <div class="col-lg-6 col-md-6 mb-4" v-if="form.service_type === 'PACKAGE'">
              <label class="form-label text-dark fs-14 fw-medium">Package</label>
              <select class="form-select" v-model="form.package_id" required>
                <option value="">Select Package</option>
                <option v-for="p in packages" :key="p.package_id" :value="p.package_id">
                  {{ p.package_name }}
                </option>
              </select>
            </div>
          </div>

          <hr class="my-4" v-if="form.service_type" />

          <!-- Dynamic Detail Fields -->
          <!-- PLAGIARISM -->
          <div class="row" v-if="form.service_type === 'PLAGIARISM'">
            <h5 class="mb-3">Plagiarism Details</h5>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Source File Path/URL</label>
              <input type="text" class="form-control" v-model="details.plagiarism.source_file" required />
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Language</label>
              <select class="form-select" v-model="details.plagiarism.language">
                <option value="id">Indonesian</option>
                <option value="en">English</option>
              </select>
            </div>
            <div class="col-lg-6 mb-4">
              <div class="form-check form-switch mb-2">
                <input class="form-check-input" type="checkbox" id="exQ" v-model="details.plagiarism.exclude_quotes">
                <label class="form-check-label" for="exQ">Exclude Quotes</label>
              </div>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="exB" v-model="details.plagiarism.exclude_bibliography">
                <label class="form-check-label" for="exB">Exclude Bibliography</label>
              </div>
            </div>
          </div>

          <!-- PARAPHRASE -->
          <div class="row" v-if="form.service_type === 'PARAPHRASE'">
            <h5 class="mb-3">Paraphrase Details</h5>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Source File Path/URL</label>
              <input type="text" class="form-control" v-model="details.paraphrase.source_file" required />
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Paraphrase Level</label>
              <select class="form-select" v-model="details.paraphrase.paraphrase_level">
                <option value="Light">Light</option>
                <option value="Medium">Medium</option>
                <option value="Heavy">Heavy</option>
              </select>
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Language</label>
              <select class="form-select" v-model="details.paraphrase.language">
                <option value="id">Indonesian</option>
                <option value="en">English</option>
              </select>
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Special Instructions</label>
              <textarea class="form-control" v-model="details.paraphrase.special_instructions" rows="2"></textarea>
            </div>
          </div>

          <!-- DOC FIX -->
          <div class="row" v-if="form.service_type === 'DOC_FIX'">
            <h5 class="mb-3">Document Fix Details</h5>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Raw File Path/URL</label>
              <input type="text" class="form-control" v-model="details.doc_fix.raw_file" required />
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Guideline File Path/URL</label>
              <input type="text" class="form-control" v-model="details.doc_fix.guideline_file" />
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Language</label>
              <select class="form-select" v-model="details.doc_fix.language">
                <option value="id">Indonesian</option>
                <option value="en">English</option>
              </select>
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Fix Notes</label>
              <textarea class="form-control" v-model="details.doc_fix.fix_notes" rows="2"></textarea>
            </div>
          </div>

          <!-- JOURNAL -->
          <div class="row" v-if="form.service_type === 'JOURNAL'">
            <h5 class="mb-3">Journal Details</h5>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Manuscript File Path/URL</label>
              <input type="text" class="form-control" v-model="details.journal.manuscript_file" required />
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Target Index</label>
              <input type="text" class="form-control" v-model="details.journal.target_index" placeholder="Sinta 2, Scopus Q3, etc." required />
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Field of Study</label>
              <input type="text" class="form-control" v-model="details.journal.field_of_study" required />
            </div>
            <div class="col-lg-6 mb-4">
              <label class="form-label">Language</label>
              <select class="form-select" v-model="details.journal.language">
                <option value="id">Indonesian</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          <!-- ALL (Combination) -->
          <div class="row" v-if="form.service_type === 'ALL'">
            <h5 class="mb-3">All Service Details</h5>
            
            <div class="col-lg-6 mb-4">
              <label class="form-label">Source File Path/URL</label>
              <input type="text" class="form-control" v-model="details.all.source_file" required />
            </div>

            <div class="col-lg-6 mb-4">
              <label class="form-label">Language</label>
              <select class="form-select" v-model="details.all.language">
                <option value="id">Indonesian</option>
                <option value="en">English</option>
              </select>
            </div>

            <div class="col-lg-6 mb-4">
              <div class="form-check form-switch mb-2">
                <input class="form-check-input" type="checkbox" id="allExQ" v-model="details.all.exclude_quotes">
                <label class="form-check-label" for="allExQ">Exclude Quotes (Plagiarism)</label>
              </div>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="allExB" v-model="details.all.exclude_bibliography">
                <label class="form-check-label" for="allExB">Exclude Bibliography (Plagiarism)</label>
              </div>
            </div>

            <div class="col-lg-6 mb-4">
              <label class="form-label">Paraphrase Level</label>
              <select class="form-select" v-model="details.all.paraphrase_level">
                <option value="Light">Light</option>
                <option value="Medium">Medium</option>
                <option value="Heavy">Heavy</option>
              </select>
            </div>

            <div class="col-lg-6 mb-4">
              <label class="form-label">Special Instructions (Paraphrase)</label>
              <textarea class="form-control" v-model="details.all.special_instructions" rows="2"></textarea>
            </div>

            <div class="col-lg-6 mb-4">
              <label class="form-label">Guideline File Path/URL (Doc Fix)</label>
              <input type="text" class="form-control" v-model="details.all.guideline_file" />
            </div>

            <div class="col-lg-6 mb-4">
              <label class="form-label">Fix Notes (Doc Fix)</label>
              <textarea class="form-control" v-model="details.all.fix_notes" rows="2"></textarea>
            </div>

            <div class="col-lg-6 mb-4">
              <label class="form-label">Target Index (Journal)</label>
              <input type="text" class="form-control" v-model="details.all.target_index" placeholder="Sinta 2, Scopus Q3, etc." required />
            </div>

            <div class="col-lg-6 mb-4">
              <label class="form-label">Field of Study (Journal)</label>
              <input type="text" class="form-control" v-model="details.all.field_of_study" required />
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/orders" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Create Order
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import customerService from '@/services/customerService';
import serviceService from '@/services/serviceService';
import packageService from '@/services/packageService';
import orderService from '@/services/orderService';
import api from '@/services/api';

export default defineComponent({
  name: 'OrderFormPage',
  components: {
    PageTitle
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    
    const customers = ref<any[]>([]);
    const services = ref<any[]>([]);
    const packages = ref<any[]>([]);
    const similarityProfiles = ref<any[]>([]);
    
    const form = ref<any>({
      customer_id: '',
      service_id: '',
      package_id: '',
      similarity_profile_id: null,
      service_classification: 'Artikel Jurnal',
      service_type: '',
      total_price: 0
    });

    const details = ref<any>({
      plagiarism: { source_file: '', exclude_quotes: false, exclude_bibliography: false, language: 'id' },
      paraphrase: { source_file: '', paraphrase_level: 'Medium', language: 'id', special_instructions: '' },
      doc_fix: { raw_file: '', guideline_file: '', language: 'id', fix_notes: '' },
      journal: { manuscript_file: '', target_index: '', field_of_study: '', language: 'id' },
      all: { 
        source_file: '', 
        language: 'id', 
        exclude_quotes: false, 
        exclude_bibliography: false, 
        paraphrase_level: 'Medium', 
        special_instructions: '', 
        guideline_file: '', 
        fix_notes: '', 
        target_index: '', 
        field_of_study: '' 
      }
    });

    onMounted(async () => {
      try {
        const [custRes, servRes, packRes, profRes] = await Promise.all([
          customerService.getAll(),
          serviceService.getAll(),
          packageService.getAll(),
          api.get('/similarity-profiles')
        ]);
        customers.value = custRes.data;
        services.value = servRes.data;
        packages.value = packRes.data;
        similarityProfiles.value = profRes.data.data || [];
      } catch (error) {
        console.error("Error fetching form dependencies", error);
      }
    });

    const onProfileSelect = () => {
      if (form.value.similarity_profile_id) {
        const p = similarityProfiles.value.find(x => x.id === form.value.similarity_profile_id);
        if (p) {
          form.value.service_classification = p.service_classification;
        }
      } else {
        form.value.service_classification = 'Artikel Jurnal';
      }
    };

    const onServiceTypeChange = () => {
      form.value.service_id = '';
      form.value.package_id = '';
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const payload = { ...form.value };
        
        if (payload.service_type === 'PLAGIARISM') {
          payload.plagiarism = details.value.plagiarism;
        } else if (payload.service_type === 'PARAPHRASE') {
          payload.paraphrase = details.value.paraphrase;
        } else if (payload.service_type === 'DOC_FIX') {
          payload.doc_fix = details.value.doc_fix;
        } else if (payload.service_type === 'JOURNAL') {
          payload.journal = details.value.journal;
        } else if (payload.service_type === 'ALL') {
          payload.all = details.value.all;
        }
        
        // Remove empty package/service id
        if (!payload.package_id) payload.package_id = null;
        if (!payload.service_id) payload.service_id = null;

        await orderService.create(payload);
        router.push('/orders');
      } catch (error) {
        console.error('Error saving order:', error);
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      details,
      loading,
      customers,
      services,
      packages,
      similarityProfiles,
      onProfileSelect,
      onServiceTypeChange,
      submitForm
    };
  }
});
</script>
